import React, { useEffect, useRef, useState } from "react";
import "./circularSlider.css";

const CircularSlider = ({ images, speed = 1 }) => {
  const trackRef = useRef(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const animationIdRef = useRef(null);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    // Duplicate items initially for seamless scroll
    const allItems = Array.from(track.children);
    allItems.forEach(item => {
      const clone = item.cloneNode(true);
      // Attach click handler to cloned items
      clone.addEventListener('click', (e) => {
        const imgElement = clone.querySelector('img');
        if (imgElement) {
          handleImageClick(imgElement.src);
        }
      });
      track.appendChild(clone);
    });

    let offset = 0;

    const step = () => {
      // Skip animation if lightbox is open
      if (lightboxOpen) return;

      offset -= speed;
      const firstChild = track.children[0];
      if (firstChild && -offset >= firstChild.offsetWidth + 20) {
        track.appendChild(firstChild);
        offset += firstChild.offsetWidth + 20;
      }
      track.style.transform = `translateX(${offset}px)`;
      animationIdRef.current = requestAnimationFrame(step);
    };

    animationIdRef.current = requestAnimationFrame(step);

    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
    };
  }, [lightboxOpen, speed]);

  // Handle ESC key to close lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && lightboxOpen) {
        handleCloseLightbox();
      }
    };

    if (lightboxOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [lightboxOpen]);

  const handleImageClick = (img) => {
    setSelectedImage(img);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains('lightbox-backdrop')) {
      handleCloseLightbox();
    }
  };

  return (
    <>
      <div className="circular-slider-wrapper">
        <div className="circular-slider-track" ref={trackRef}>
          {images.map((img, idx) => (
            <div 
              className="circular-slider-item" 
              key={idx} 
              onClick={() => handleImageClick(img)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleImageClick(img);
              }}
            >
              <img src={img} alt={`item-${idx}`} />
            </div>
          ))}
        </div>
      </div>

      {lightboxOpen && (
        <div className="lightbox-backdrop" onClick={handleBackdropClick}>
          <div className="lightbox-container">
            <button className="lightbox-close" onClick={handleCloseLightbox}>
              ✕
            </button>
            <div className="lightbox-content">
              <img src={selectedImage} alt="enlarged" className="lightbox-image" />
            </div>
            <div className="lightbox-hint">
              Press ESC or click outside to close
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CircularSlider;
